import React, { useState, useRef, useEffect } from 'react';
import { Users, MessageSquare, Heart, Calendar, Reply, Flame, Lightbulb, DollarSign, TrendingUp, Package, Code, Grid3X3, MessageCircle, LifeBuoy, Search, SortAsc, X, Headphones } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import SimpleTextFormatter from '@/components/community/SimpleTextFormatter';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import DashboardButton from '@/components/ui/DashboardButton';
import SidebarLayout from '@/components/layout/SidebarLayout';
import MobilePageWrapper from '@/components/ui/MobilePageWrapper';
import DashboardCard from '@/components/ui/DashboardCard';
import AvatarPics from '@/components/ui/AvatarPics';
import GlassPill from '@/components/ui/GlassPill';
import { getAppDescription } from '@/config/app-registry';
import NewPostModal from '@/components/community/NewPostModal';
import CommunityAppRequestsContainer from '@/components/community/CommunityAppRequestsContainer';
import { useCommunityCategories, useCategoryCounts, useCommunityDiscussions, useToggleDiscussionLike } from '@/hooks/community/use-community-discussions';
import { useCommunityDiscussionsInfinite } from '@/hooks/community/use-community-discussions-paginated';
import InfiniteScrollContainer from '@/components/community/InfiniteScrollContainer';
import { AppLibraryProvider } from '@/components/app-library/AppLibraryContext';
import { AppIdeaContentLightbox } from '@/components/app-ideas/lightbox';
import { useAppIdeaFavorites } from '@/hooks/app-library/use-app-idea-favorites';
import { Input } from '@/components/ui/input';
import { StandardSelect, SelectOption } from '@/components/ui/form-fields/StandardSelect';
import { Separator } from '@/components/ui/separator';
import CommunityProfileLightbox from '@/components/community/CommunityProfileLightbox';
import { CommunityProfilePicture, CommunityUser, CommunityProfileContext } from '@/types/community-profile';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { useModalTrigger } from '@/hooks/navigation/use-modal-trigger';
import { URL_PARAMS } from '@/config/url-parameters';

// Community Search and Sort Component
const CommunitySearchAndSort: React.FC<{
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  sortOrder: string;
  onSortOrderChange: (order: string) => void;
}> = ({ searchQuery, onSearchChange, sortBy, onSortChange, sortOrder, onSortOrderChange }) => {
  
  const sortOptions: SelectOption[] = [
    { value: 'created_at', label: 'Recent Posts' },
    { value: 'likes', label: 'Most Liked' },
    { value: 'replies', label: 'Most Discussed' },
    { value: 'updated_at', label: 'Recently Updated' },
    { value: 'title', label: 'Alphabetical' },
  ];

  const sortOrderOptions: SelectOption[] = [
    { value: 'desc', label: 'Descending' },
    { value: 'asc', label: 'Ascending' },
  ];

  return (
    <div className="hidden lg:block relative p-3 md:p-6 rounded-lg overflow-hidden">
      {/* Gradient background - matching card border radius */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
      
      {/* Top border highlight - matching card border radius */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-300/30 to-transparent dark:via-blue-500/20 rounded-t-lg" />
      
      {/* Glass effect - matching card border radius */}
      <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10 rounded-lg" />



      <div className="space-y-4">
        {/* Search Input */}
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-20">
            <Search size={16} className="text-accent" />
          </div>
          <Input 
            placeholder="Search posts..." 
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 pr-10 h-10 text-sm font-medium bg-white/30 dark:bg-white/10 backdrop-blur-sm border border-accent/20 focus:border-accent/40 hover:border-accent/30"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 text-accent hover:text-accent/70 transition-colors"
              title="Clear search"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Sort Options */}
        <div className="grid grid-cols-1 gap-3">
          <StandardSelect
            id="sort-by"
            label="Sort By"
            icon={<SortAsc size={16} />}
            value={sortBy}
            onChange={(value) => onSortChange(Array.isArray(value) ? value[0] || 'created_at' : value)}
            options={sortOptions}
            placeholder="Select sort option"
            colorVariant="blue"
          />
          
          <StandardSelect
            id="sort-order"
            label="Order"
            icon={<SortAsc size={16} />}
            value={sortOrder}
            onChange={(value) => onSortOrderChange(Array.isArray(value) ? value[0] || 'desc' : value)}
            options={sortOrderOptions}
            placeholder="Select order"
            colorVariant="blue"
          />
        </div>
      </div>
    </div>
  );
};

// Shared helper functions for categories
const getCategoryStyle = (categoryName: string): { color: string; iconName: string } => {
  const styleMap: Record<string, { color: string; iconName: string }> = {
    'General': { color: 'blue', iconName: 'MessageSquare' },
    'Ideation': { color: 'purple', iconName: 'Lightbulb' },
    'Funding': { color: 'green', iconName: 'DollarSign' },
    'Marketing': { color: 'orange', iconName: 'TrendingUp' },
    'Product': { color: 'cyan', iconName: 'Package' },
    'Technology': { color: 'indigo', iconName: 'Code' },
    'Feedback': { color: 'pink', iconName: 'MessageCircle' },
    'Support': { color: 'emerald', iconName: 'LifeBuoy' },
  };
  return styleMap[categoryName] || { color: 'blue', iconName: 'MessageSquare' };
};

const getIconComponent = (iconName: string) => {
  const iconMap: Record<string, React.ComponentType<any>> = {
    'MessageSquare': MessageSquare,
    'Lightbulb': Lightbulb,
    'DollarSign': DollarSign,
    'TrendingUp': TrendingUp,
    'Package': Package,
    'Code': Code,
    'MessageCircle': MessageCircle,
    'LifeBuoy': LifeBuoy,
  };
  return iconMap[iconName] || MessageSquare;
};

// Main Categories Container (General, Ideation, Marketing, Technology)
const MainCategoriesContainer: React.FC<{ 
  categoryCounts?: Record<string, number>;
  selectedCategory?: string;
  onCategoryChange: (category: string | null) => void;
}> = ({ categoryCounts = {}, selectedCategory, onCategoryChange }) => {
  const { data: categories = [], isLoading: categoriesLoading } = useCommunityCategories();
  
  // Filter main categories
  const mainCategories = categories.filter(category => 
    ['General', 'Ideation', 'Marketing', 'Technology'].includes(category.name)
  );
  
  if (categoriesLoading) {
    return (
      <div className="relative p-3 md:p-6 rounded-lg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="text-blue-500 dark:text-blue-400" size={18} />
          <h3 className="font-medium text-base">Categories</h3>
        </div>
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="p-3 rounded-lg bg-white/40 dark:bg-white/5 border border-white/20 dark:border-white/10 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-slate-300 dark:bg-slate-600 rounded"></div>
                  <div className="w-24 h-4 bg-slate-300 dark:bg-slate-600 rounded"></div>
                </div>
                <div className="w-8 h-5 bg-slate-300 dark:bg-slate-600 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative p-3 md:p-6 rounded-lg overflow-hidden">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
      
      {/* Top border highlight */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-300/30 to-transparent dark:via-blue-500/20 rounded-t-lg" />
      
      {/* Glass effect */}
      <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10 rounded-lg" />

      {/* Categories List */}
      <div className="space-y-2">
        {mainCategories.map((category) => {
          const { color, iconName } = getCategoryStyle(category.name);
          const IconComponent = getIconComponent(iconName);
          const count = categoryCounts[category.name] || 0;
          const isActive = selectedCategory === category.name;
          
          return (
            <motion.div
              key={category.id}
              whileHover={{ scale: 1.02, x: 4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onCategoryChange(category.name)}
              className={`p-3 rounded-lg cursor-pointer transition-all duration-200 group
                ${isActive
                  ? `${color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700' : ''}
                     ${color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700' : ''}
                     ${color === 'green' ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700' : ''}
                     ${color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700' : ''}
                     ${color === 'cyan' ? 'bg-cyan-100 dark:bg-cyan-900/30 border-cyan-300 dark:border-cyan-700' : ''}
                     ${color === 'indigo' ? 'bg-indigo-100 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700' : ''}
                     ${color === 'pink' ? 'bg-pink-100 dark:bg-pink-900/30 border-pink-300 dark:border-pink-700' : ''}`
                  : 'bg-white/40 dark:bg-white/5 hover:bg-white/60 dark:hover:bg-white/10'
                }
                border border-white/20 dark:border-white/10
                hover:border-white/40 dark:hover:border-white/20`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <IconComponent className={`h-5 w-5 ${
                    isActive
                      ? `${color === 'blue' ? 'text-blue-600 dark:text-blue-400' : ''}
                         ${color === 'purple' ? 'text-purple-600 dark:text-purple-400' : ''}
                         ${color === 'green' ? 'text-green-600 dark:text-green-400' : ''}
                         ${color === 'orange' ? 'text-orange-600 dark:text-orange-400' : ''}
                         ${color === 'cyan' ? 'text-cyan-600 dark:text-cyan-400' : ''}
                         ${color === 'indigo' ? 'text-indigo-600 dark:text-indigo-400' : ''}
                         ${color === 'pink' ? 'text-pink-600 dark:text-pink-400' : ''}
                         ${color === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : ''}`
                      : `${color === 'blue' ? 'text-blue-500 dark:text-blue-400' : ''}
                         ${color === 'purple' ? 'text-purple-500 dark:text-purple-400' : ''}
                         ${color === 'green' ? 'text-green-500 dark:text-green-400' : ''}
                         ${color === 'orange' ? 'text-orange-500 dark:text-orange-400' : ''}
                         ${color === 'cyan' ? 'text-cyan-500 dark:text-cyan-400' : ''}
                         ${color === 'indigo' ? 'text-indigo-500 dark:text-indigo-400' : ''}
                         ${color === 'pink' ? 'text-pink-500 dark:text-pink-400' : ''}
                         ${color === 'emerald' ? 'text-emerald-500 dark:text-emerald-400' : ''}`
                  }`} />
                  <span className={`text-sm font-medium ${
                    isActive
                      ? `${color === 'blue' ? 'text-blue-900 dark:text-blue-100' : ''}
                         ${color === 'purple' ? 'text-purple-900 dark:text-purple-100' : ''}
                         ${color === 'green' ? 'text-green-900 dark:text-green-100' : ''}
                         ${color === 'orange' ? 'text-orange-900 dark:text-orange-100' : ''}
                         ${color === 'cyan' ? 'text-cyan-900 dark:text-cyan-100' : ''}
                         ${color === 'indigo' ? 'text-indigo-900 dark:text-indigo-100' : ''}
                         ${color === 'pink' ? 'text-pink-900 dark:text-pink-100' : ''}
                         ${color === 'emerald' ? 'text-emerald-900 dark:text-emerald-100' : ''}`
                      : 'text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-slate-100'
                  }`}>
                    {category.name}
                  </span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium
                  ${isActive
                    ? `${color === 'blue' ? 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200' : ''}
                       ${color === 'purple' ? 'bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200' : ''}
                       ${color === 'green' ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200' : ''}
                       ${color === 'orange' ? 'bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200' : ''}
                       ${color === 'cyan' ? 'bg-cyan-200 dark:bg-cyan-800 text-cyan-800 dark:text-cyan-200' : ''}
                       ${color === 'indigo' ? 'bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200' : ''}
                       ${color === 'pink' ? 'bg-pink-200 dark:bg-pink-800 text-pink-800 dark:text-pink-200' : ''}
                       ${color === 'emerald' ? 'bg-emerald-200 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200' : ''}`
                    : `${color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : ''}
                       ${color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : ''}
                       ${color === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : ''}
                       ${color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' : ''}
                       ${color === 'cyan' ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300' : ''}
                       ${color === 'indigo' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : ''}
                       ${color === 'pink' ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300' : ''}
                       ${color === 'emerald' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : ''}`
                  }
                `}>
                  {count}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// Support Categories Container (Feedback, Support)
const SupportCategoriesContainer: React.FC<{ 
  categoryCounts?: Record<string, number>;
  selectedCategory?: string;
  onCategoryChange: (category: string | null) => void;
}> = ({ categoryCounts = {}, selectedCategory, onCategoryChange }) => {
  const { data: categories = [], isLoading: categoriesLoading } = useCommunityCategories();
  
  // Filter support categories and sort to show Support first
  const supportCategories = categories.filter(category => 
    ['Feedback', 'Support'].includes(category.name)
  ).sort((a, b) => {
    // Sort to ensure Support comes before Feedback
    if (a.name === 'Support') return -1;
    if (b.name === 'Support') return 1;
    return 0;
  });
  
  if (categoriesLoading) {
    return (
      <div className="relative p-3 md:p-6 rounded-lg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle className="text-pink-500 dark:text-pink-400" size={18} />
          <h3 className="font-medium text-base">Help & Support</h3>
        </div>
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="p-3 rounded-lg bg-white/40 dark:bg-white/5 border border-white/20 dark:border-white/10 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-slate-300 dark:bg-slate-600 rounded"></div>
                  <div className="w-24 h-4 bg-slate-300 dark:bg-slate-600 rounded"></div>
                </div>
                <div className="w-8 h-5 bg-slate-300 dark:bg-slate-600 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative p-3 md:p-6 rounded-lg overflow-hidden">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
      
      {/* Top border highlight - using pink/emerald gradient */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-pink-300/30 to-transparent dark:via-pink-500/20 rounded-t-lg" />
      
      {/* Glass effect */}
      <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10 rounded-lg" />



      {/* Categories List */}
      <div className="space-y-2">
        {supportCategories.map((category) => {
          const { color, iconName } = getCategoryStyle(category.name);
          const IconComponent = getIconComponent(iconName);
          const count = categoryCounts[category.name] || 0;
          const isActive = selectedCategory === category.name;
          
          return (
            <motion.div
              key={category.id}
              whileHover={{ scale: 1.02, x: 4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onCategoryChange(category.name)}
              className={`p-3 rounded-lg cursor-pointer transition-all duration-200 group
                ${isActive
                  ? `${color === 'pink' ? 'bg-pink-100 dark:bg-pink-900/30 border-pink-300 dark:border-pink-700' : ''}
                     ${color === 'emerald' ? 'bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700' : ''}`
                  : 'bg-white/40 dark:bg-white/5 hover:bg-white/60 dark:hover:bg-white/10'
                }
                border border-white/20 dark:border-white/10
                hover:border-white/40 dark:hover:border-white/20`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <IconComponent className={`h-5 w-5 ${
                    isActive
                      ? `${color === 'pink' ? 'text-pink-600 dark:text-pink-400' : ''}
                         ${color === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : ''}`
                      : `${color === 'pink' ? 'text-pink-500 dark:text-pink-400' : ''}
                         ${color === 'emerald' ? 'text-emerald-500 dark:text-emerald-400' : ''}`
                  }`} />
                  <span className={`text-sm font-medium ${
                    isActive
                      ? `${color === 'pink' ? 'text-pink-900 dark:text-pink-100' : ''}
                         ${color === 'emerald' ? 'text-emerald-900 dark:text-emerald-100' : ''}`
                      : 'text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-slate-100'
                  }`}>
                    {category.name}
                  </span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium
                  ${isActive
                    ? `${color === 'pink' ? 'bg-pink-200 dark:bg-pink-800 text-pink-800 dark:text-pink-200' : ''}
                       ${color === 'emerald' ? 'bg-emerald-200 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200' : ''}`
                    : `${color === 'pink' ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300' : ''}
                       ${color === 'emerald' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : ''}`
                  }
                `}>
                  {count}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// Main Community Sidebar component
const CommunitySidebar: React.FC<{ 
  categoryCounts?: Record<string, number>;
  selectedCategory?: string;
  onCategoryChange: (category: string | null) => void;
  onAppIdeaClick?: (app: any) => void;
  onViewAllAppRequests?: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  sortOrder: string;
  onSortOrderChange: (order: string) => void;
}> = ({ 
  categoryCounts, 
  selectedCategory, 
  onCategoryChange,
  onAppIdeaClick,
  onViewAllAppRequests,
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  sortOrder,
  onSortOrderChange
}) => {
  return (
    <div className="space-y-3 md:space-y-6">
      {/* TEMPORARILY HIDDEN - Main Categories (General, Ideation, Marketing, Technology)
          To reactivate: Uncomment the MainCategoriesContainer below
      <MainCategoriesContainer 
        categoryCounts={categoryCounts} 
        selectedCategory={selectedCategory}
        onCategoryChange={onCategoryChange}
      />
      */}
      <SupportCategoriesContainer 
        categoryCounts={categoryCounts} 
        selectedCategory={selectedCategory}
        onCategoryChange={onCategoryChange}
      />
      <CommunitySearchAndSort 
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        sortBy={sortBy}
        onSortChange={onSortChange}
        sortOrder={sortOrder}
        onSortOrderChange={onSortOrderChange}
      />
      {/* Hide App Requests section on mobile/tablet when sidebar is above content */}
      <div className="hidden lg:block">
        <CommunityAppRequestsContainer
          onAppIdeaClick={onAppIdeaClick}
          onViewAllAppRequests={onViewAllAppRequests}
        />
      </div>
    </div>
  );
};

// Beautiful Post Card Component - Matching DiscussionThread design
const PostCard: React.FC<{
  id: string;
  title: string;
  content: string;
  author: { name: string; avatar?: string; initials: string; userId?: string };
  timestamp: Date;
  category: { name: string; color: string };
  stats: { replies: number; likes: number };
  isHot?: boolean;
  isLiked?: boolean;
  onAvatarClick: (author: { name: string; avatar?: string; userId?: string }, discussionId: string) => void;
}> = ({ id, title, content, author, timestamp, category, stats, isHot, isLiked = false, onAvatarClick }) => {
  const navigate = useNavigate();
  const toggleDiscussionLike = useToggleDiscussionLike();
  
  const handleClick = () => {
    navigate(`/community/post/${id}`);
  };
  
  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking like button
    toggleDiscussionLike.mutate(id);
  };
  
  const handleAvatarClickLocal = () => {
    onAvatarClick(author, id);
  };
  
  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
      onClick={handleClick}
      className="group relative bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200 border border-slate-200 dark:border-slate-700 cursor-pointer"
    >
      <div className="flex items-start gap-3">
        <AvatarPics
          profilePictureUrl={author.avatar}
          initials={author.initials}
          className="h-10 w-10 flex-shrink-0"
          onClick={handleAvatarClickLocal}
          clickable={!!(author.avatar && author.userId)}
        />
        
                <div className="flex-1 min-w-0">
          {/* Post title at the top */}
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2 mb-2">
            {title}
          </h3>
          
          {/* Post content */}
          <div className="prose-sm max-w-none text-slate-700 dark:text-slate-300 text-sm leading-relaxed mb-3 line-clamp-2 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            <SimpleTextFormatter content={content} className="[&>p]:my-1 [&>p]:first:mt-0 [&>p]:last:mb-0" />
          </div>
          
          {/* Footer with author info and actions */}
          <div className="flex items-center gap-4">
            {/* Author info and timestamp */}
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {author.name}
            </span>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {formatDistanceToNow(timestamp, { addSuffix: true })}
            </span>
            
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleLike}
              className={`flex items-center gap-1.5 text-sm transition-colors ${
                isLiked 
                  ? 'text-red-500 hover:text-red-600' 
                  : 'text-slate-500 hover:text-red-500'
              }`}
            >
              <Heart size={16} className={isLiked ? 'fill-red-500' : ''} />
              <span>{stats.likes || 0}</span>
            </motion.button>
            
            <button className="flex items-center gap-1 text-sm text-slate-500 hover:text-blue-500 transition-colors">
              <MessageSquare size={16} />
              {stats.replies || 0}{stats.replies === 1 ? ' reply' : stats.replies > 1 ? ' replies' : ''}
            </button>
            
            {/* Category tag */}
            <div className="flex items-center gap-1 ml-auto mr-2">
              {isHot && <Flame className="h-4 w-4 text-orange-500" />}
              <span className={`text-xs px-2 py-1 rounded-full font-medium
                ${category.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : ''}
                ${category.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : ''}
                ${category.color === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : ''}
                ${category.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' : ''}
                ${category.color === 'cyan' ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300' : ''}
                ${category.color === 'indigo' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : ''}
                ${category.color === 'pink' ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300' : ''}
                ${category.color === 'emerald' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : ''}
              `}>
                {category.name}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Create Post Call-to-Action Component
const CreatePostCTA: React.FC<{ onCreatePost: () => void }> = ({ onCreatePost }) => {
  return (
    <DashboardCard className="text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="py-8"
      >
        <motion.div
          animate={{ y: [0, -2, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 mb-4"
        >
          <MessageSquare className="h-8 w-8 text-white" />
        </motion.div>
        
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
          Start a Post
        </h3>
        <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-md mx-auto">
          Share your ideas, ask questions, or help fellow entrepreneurs grow their businesses
        </p>
        
        <motion.button
          whileHover={{ scale: 1.05, y: -1 }}
          whileTap={{ scale: 0.95 }}
          onClick={onCreatePost}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
        >
          <MessageSquare size={16} />
          Create New Post
        </motion.button>
      </motion.div>
    </DashboardCard>
  );
};

// Helper functions for dynamic titles, descriptions, and icons using database data
const getDynamicTitle = (selectedCategory?: string, categories: any[] = []): string => {
  if (!selectedCategory) {
    return "🔥 What's Happening Right Now";
  }
  
  const category = categories.find(cat => cat.name === selectedCategory);
  return category?.name || selectedCategory;
};

const getDynamicDescription = (selectedCategory?: string, categories: any[] = []): string => {
  if (!selectedCategory) {
    return "The latest conversations, ideas, and updates from the community - jump in and see what everyone's building";
  }
  
  const category = categories.find(cat => cat.name === selectedCategory);
  return category?.description || `Join the conversation about ${selectedCategory.toLowerCase()} and connect with fellow entrepreneurs`;
};

const getDynamicIcon = (selectedCategory?: string, categories: any[] = []): JSX.Element => {
  if (!selectedCategory) {
    return <MessageSquare className="text-blue-500 dark:text-blue-400" size={20} />;
  }
  
  const category = categories.find(cat => cat.name === selectedCategory);
  const iconName = category?.icon;
  
  if (iconName) {
    const IconComponent = getIconComponent(iconName);
    const { color } = getCategoryStyle(selectedCategory);
    return <IconComponent className={`text-${color}-500 dark:text-${color}-400`} size={20} />;
  }
  
  // Fallback to MessageSquare if no icon found
  return <MessageSquare className="text-blue-500 dark:text-blue-400" size={20} />;
};

// Main Community Content Component
const CommunityMainContent: React.FC<{ 
  onCreatePost: () => void;
  selectedCategory?: string;
  searchQuery?: string;
  sortBy?: string;
  sortOrder?: string;
}> = ({ onCreatePost, selectedCategory, searchQuery, sortBy, sortOrder }) => {
  // Profile lightbox state for all posts
  const [isProfileLightboxOpen, setIsProfileLightboxOpen] = useState(false);
  const [profileLightboxImage, setProfileLightboxImage] = useState<CommunityProfilePicture | null>(null);
  const [profileLightboxContext, setProfileLightboxContext] = useState<CommunityProfileContext | null>(null);
  // Fetch categories data for dynamic titles and descriptions
  const { data: categories = [] } = useCommunityCategories();
  
  // Use the new infinite scroll hook with category filtering, search, and sorting
  const { 
    data: discussions = [], 
    isLoading, 
    error, 
    hasNextPage, 
    fetchNextPage, 
    isFetchingNextPage,
    categoryCounts 
  } = useCommunityDiscussionsInfinite({ 
    pageSize: 20,
    category: selectedCategory || undefined, // Pass undefined instead of null for "all categories"
    searchQuery: searchQuery || undefined,
    sortBy: sortBy || 'created_at',
    sortOrder: (sortOrder as 'asc' | 'desc') || 'desc'
  });
  
  // Helper function to get user initials from name
  const getInitials = (firstName?: string) => {
    if (firstName && firstName.length >= 2) {
      return firstName.slice(0, 2).toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase() + 'U';
    }
    return 'U';
  };
  
  // Helper function to get user name
  const getUserName = (firstName?: string) => {
    if (firstName) {
      return firstName;
    }
    return 'Anon';
  };

  // Helper function to get category style
  const getCategoryStyle = (categoryName: string): { color: string; iconName: string } => {
    const styleMap: Record<string, { color: string; iconName: string }> = {
      'General': { color: 'blue', iconName: 'MessageSquare' },
      'Ideation': { color: 'purple', iconName: 'Lightbulb' },
      'Funding': { color: 'green', iconName: 'DollarSign' },
      'Marketing': { color: 'orange', iconName: 'TrendingUp' },
      'Product': { color: 'cyan', iconName: 'Package' },
      'Technology': { color: 'indigo', iconName: 'Code' },
      'Feedback': { color: 'pink', iconName: 'MessageCircle' },
      'Support': { color: 'emerald', iconName: 'LifeBuoy' },
    };
    return styleMap[categoryName] || { color: 'blue', iconName: 'MessageSquare' };
  };

  // Profile lightbox handlers
  const handleAvatarClick = (author: { name: string; avatar?: string; userId?: string }, discussionId: string) => {
    if (author.avatar && author.userId) {
      const profileImage: CommunityProfilePicture = {
        id: `profile-${author.userId}`,
        url: author.avatar,
        userId: author.userId,
        userName: author.name,
        alt: `${author.name}'s profile picture`
      };
      
      const context: CommunityProfileContext = {
        user: {
          id: author.userId,
          first_name: author.name,
          profile_picture_url: author.avatar
        },
        discussionId,
        isAuthor: true
      };
      
      setProfileLightboxImage(profileImage);
      setProfileLightboxContext(context);
      setIsProfileLightboxOpen(true);
    }
  };

  const handleCloseLightbox = () => {
    setIsProfileLightboxOpen(false);
    setProfileLightboxImage(null);
    setProfileLightboxContext(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-3 md:space-y-6">
        <DashboardCard>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">Loading posts...</p>
            </div>
          </div>
        </DashboardCard>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3 md:space-y-6">
        <DashboardCard>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-red-600 dark:text-red-400">Error loading posts</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">Please try again later</p>
            </div>
          </div>
        </DashboardCard>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-6">
      {/* Recent Posts - Add anchor for mobile scrolling */}
      <div id="community-posts" className="scroll-mt-20">
        <DashboardCard>
          {/* Header with New Post button */}
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="flex items-center gap-2">
              {getDynamicIcon(selectedCategory, categories)}
              <h3 className="font-medium text-base">
                {getDynamicTitle(selectedCategory, categories)}
              </h3>
              {(() => {
                // Get current count based on selected category
                const currentCount = selectedCategory 
                  ? (categoryCounts?.[selectedCategory] || 0)
                  : discussions.length;
                
                // Get the color for the current category
                const categoryColor = selectedCategory 
                  ? getCategoryStyle(selectedCategory).color
                  : 'blue'; // Default to blue for "All" view
                
                return currentCount > 0 && (
                  <GlassPill 
                    type={currentCount.toString()} 
                    variant={categoryColor as any} 
                    className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
                  />
                );
              })()}
            </div>
            
            {/* New Post Button */}
            <DashboardButton
              onClick={onCreatePost}
              icon={MessageSquare}
              size="sm"
              className="h-8 px-3 py-1.5 text-sm"
            >
              New Post
            </DashboardButton>
          </div>
          
          {discussions.length === 0 && !isLoading ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                No posts yet
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Be the first to start a conversation in the community!
              </p>
            </div>
          ) : (
            <InfiniteScrollContainer
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              isLoading={isLoading}
              error={error}
            >
              <div className="space-y-4">
                {discussions.map((discussion) => {
                  // Transform database discussion to PostCard format
                  const transformedPost = {
                    id: discussion.id,
                    title: discussion.title,
                    content: discussion.content,
                    author: {
                      name: getUserName(discussion.users?.first_name),
                      initials: getInitials(discussion.users?.first_name),
                      avatar: discussion.users?.profile_picture_url,
                      userId: discussion.users?.id
                    },
                    timestamp: new Date(discussion.created_at),
                    category: { 
                      name: discussion.category, 
                      color: getCategoryStyle(discussion.category).color 
                    },
                    stats: { 
                      replies: discussion.replies || 0, 
                      likes: discussion.likes || 0 
                    },
                    isHot: discussion.is_hot || false,
                    isLiked: discussion.is_liked_by_user || false
                  };
                  
                  return <PostCard key={discussion.id} {...transformedPost} onAvatarClick={handleAvatarClick} />;
                })}
              </div>
            </InfiniteScrollContainer>
          )}
        </DashboardCard>
      </div>
      
      {/* Create Post CTA */}
      <CreatePostCTA onCreatePost={onCreatePost} />
      
      {/* Community Profile Lightbox - Rendered outside PostCards to avoid click bubbling */}
      {profileLightboxImage && profileLightboxContext && (
        <CommunityProfileLightbox
          currentImage={profileLightboxImage}
          isLightboxOpen={isProfileLightboxOpen}
          profileContext={profileLightboxContext}
          onClose={handleCloseLightbox}
        />
      )}
    </div>
  );
};

const Community: React.FC = () => {
  const navigate = useNavigate();
  const [isNewPostModalOpen, setIsNewPostModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>('Support'); // Default to Support category
  const { isMobile } = useIsMobile();
  
  // URL parameter modal trigger detection
  const { shouldOpen: shouldOpenPostModal } = useModalTrigger(URL_PARAMS.MODALS.CREATE_POST);
  
  // Search and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // App Requests modal state
  const [isAppIdeaLightboxOpen, setIsAppIdeaLightboxOpen] = useState(false);
  const [selectedAppIdea, setSelectedAppIdea] = useState<any>(null);
  
  // Get full app ideas data for lightbox navigation
  const { appIdeas: allAppIdeas } = useAppIdeaFavorites();
  
  // Get category counts for sidebar - now using the selected category filter
  const { categoryCounts } = useCommunityDiscussionsInfinite({ 
    pageSize: 1, 
    enabled: true,
    category: undefined // Always get all categories for the sidebar counts
  });

  const handleNewPost = () => {
    setIsNewPostModalOpen(true);
  };

  // Handle URL parameter trigger for post modal
  useEffect(() => {
    if (shouldOpenPostModal) {
      console.log('[Community] URL parameter detected, opening post modal');
      setIsNewPostModalOpen(true);
    }
  }, [shouldOpenPostModal]);

  // Enhanced category change handler with mobile scroll functionality
  const handleCategoryChange = (category: string | null) => {
    setSelectedCategory(category);
    
    // Only scroll on mobile/tablet (below lg breakpoint - 1024px)
    // Check if screen width is less than 1024px (lg breakpoint)
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      // Small delay to allow React to update the UI first
      setTimeout(() => {
        const postsSection = document.getElementById('community-posts');
        if (postsSection) {
          postsSection.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  };

  const handleAppIdeaClick = (app: any) => {
    console.log('[Community] App idea clicked:', {
      id: app.id,
      name: app.name,
      hasCitations: !!app.research_citations,
      citationsCount: app.research_citations?.length || 0,
      hasResearchOverview: !!app.research_overview,
      hasResearchRequirements: !!app.research_requirements,
      hasResearchFeatures: !!app.research_enhanced_features,
      hasRelatedQuestions: !!app.research_related_questions,
      relatedQuestionsCount: app.research_related_questions?.length || 0
    });
    
    setSelectedAppIdea(app);
    setIsAppIdeaLightboxOpen(true);
  };

  const handleViewAllAppRequests = () => {
    // Navigate to App Library page using React Router for smooth transition
    navigate('/app-library');
  };

  const handleCloseLightbox = () => {
    setIsAppIdeaLightboxOpen(false);
    setSelectedAppIdea(null);
  };

  return (
    <AppLibraryProvider>
      <MobilePageWrapper>
        <Container>
          <DashboardHeader
            title="Support"
            icon={<Headphones size={24} />}
            description="Get help, share feedback, and connect with our support community"
          />
          
          <div className="space-y-3 md:space-y-6">
            <SidebarLayout
              sidebar={
                <CommunitySidebar 
                  categoryCounts={categoryCounts} 
                  selectedCategory={selectedCategory || undefined}
                  onCategoryChange={handleCategoryChange}
                  onAppIdeaClick={handleAppIdeaClick}
                  onViewAllAppRequests={handleViewAllAppRequests}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  sortOrder={sortOrder}
                  onSortOrderChange={setSortOrder}
                />
              }
            >
              <CommunityMainContent 
                onCreatePost={handleNewPost} 
                selectedCategory={selectedCategory || undefined}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            </SidebarLayout>
          </div>
        </Container>

        {/* New Post Modal */}
        <NewPostModal
          isOpen={isNewPostModalOpen}
          onClose={() => setIsNewPostModalOpen(false)}
          selectedCategory={selectedCategory || undefined}
        />

        {/* App Idea Lightbox */}
        <AppIdeaContentLightbox
          idea={selectedAppIdea}
          isOpen={isAppIdeaLightboxOpen}
          onClose={handleCloseLightbox}
          allIdeas={allAppIdeas || []} // Pass full app ideas for navigation
          onNavigate={(idea) => setSelectedAppIdea(idea)}
          isLoading={false}
          hasNextPage={false}
          isFetchingNextPage={false}
        />
      </MobilePageWrapper>
    </AppLibraryProvider>
  );
};

export default Community; 